import { FC, useCallback, useRef } from 'react';
import { useRouter } from 'shared-routing';
import { DiningListTemplate } from 'voyager';
import type { JourneyPickerSearchParams } from 'voyager';

import SectionContainer from '@common/components/SectionContainer';
import { Routes } from '@common/constants';
import {
  EntitiesListingWrapper,
  JourneyPickerInnerWrapper,
  JourneyPickerOuterWrapper,
} from '@features/EntitiesListing/EntitiesListings.styles';
import { JourneyPickerV2 } from '@features/JourneyPickerV2';
import { useCategoryNavigation } from '@features/JourneyPickerV2/useCategoryNavigation';
import { useNavCategories } from '@features/JourneyPickerV2/useNavCategories';
import { useGetTablesPage } from '@features/tables/hooks/useGetTablesPage';
import { useTranslations } from '@lang';
import { parseDateToString } from '@utils/helpers';

import type { DiningV2Props } from './DiningV2.types';
import { usePropertyContext } from './queries';
import { useDiningList } from './useDiningList';

export const DiningV2: FC<DiningV2Props> = ({ changeJourneyParam }) => {
  const { t } = useTranslations();
  const router = useRouter();
  const { items, isLoading } = useDiningList();

  const { propertyId, language } = usePropertyContext();
  const { data: tablesPage } = useGetTablesPage(propertyId, language);
  const tablesPageTitle = (tablesPage as { title?: string } | null | undefined)?.title;

  const navCategories = useNavCategories();
  const handleNavCategoryChange = useCategoryNavigation();

  const searchRef = useRef<HTMLDivElement>(null);
  const handleEditSearch = useCallback(() => {
    searchRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const goToReservation = (restaurantId: string) =>
    router.push({
      pathname: Routes.TABLES_BOOK_RESTAURANT.replace('[restaurant]', restaurantId),
    });

  const handleSearchComplete = useCallback(
    (params: JourneyPickerSearchParams) => {
      changeJourneyParam('fromDate', parseDateToString(params.fromDate));
      changeJourneyParam('tableAdults', params.adults);
      changeJourneyParam('tableChildren', params.children ?? 0);
    },
    [changeJourneyParam]
  );

  return (
    <EntitiesListingWrapper>
      <JourneyPickerOuterWrapper ref={searchRef}>
        <JourneyPickerInnerWrapper>
          <JourneyPickerV2
            dateMode="single"
            dateContext="dining"
            showGuests
            categories={navCategories}
            category="tables"
            onCategoryChange={handleNavCategoryChange}
            onSearchComplete={handleSearchComplete}
          />
        </JourneyPickerInnerWrapper>
      </JourneyPickerOuterWrapper>
      <SectionContainer>
        <DiningListTemplate
          items={items}
          isLoading={isLoading}
          onSelectItem={goToReservation}
          onEditSearch={handleEditSearch}
          labels={{
            title: tablesPageTitle || t('app.dining'),
            quickView: t('dining.quickView'),
            reserve: t('dining.reserve'),
            viewMenu: t('dining.viewMenu'),
            unavailable: t('dining.unavailableForSearch'),
            editSearch: t('dining.editSearch'),
            noResults: t('dining.noResults'),
            noImage: t('basket.noImageLabel'),
            close: t('dining.close'),
            previousImage: t('product.previousImage'),
            nextImage: t('product.nextImage'),
            openingHours: t('dining.openingHours'),
            menus: t('dining.menus'),
            showMore: t('dining.showMore'),
            showLess: t('dining.showLess'),
            today: t('common.today'),
          }}
        />
      </SectionContainer>
    </EntitiesListingWrapper>
  );
};
